import { useState, type ReactNode } from "react";
import { inputClass } from "../routes/SignIn.js";

/**
 * Self-contained UI primitives for the AI onboarding wizard. The contractor
 * web app has no shared component library, so these live here. Brand tokens
 * (brand-*, charcoal, field-gray) come from index.css.
 */

export function cn(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

export function SparkleIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M12 2l1.6 5.4L19 9l-5.4 1.6L12 16l-1.6-5.4L5 9l5.4-1.6L12 2z" />
      <path d="M19 14l.8 2.7L22.5 17.5l-2.7.8L19 21l-.8-2.7L15.5 17.5l2.7-.8L19 14z" opacity=".7" />
    </svg>
  );
}

/** Small "AI filled this in" affordance — the magic signal on each card. */
export function AiBadge({ label = "AI-filled" }: { label?: string }) {
  return (
    <span className="ml-auto inline-flex shrink-0 items-center gap-1 rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700">
      <SparkleIcon className="h-3 w-3" />
      {label}
    </span>
  );
}

export function Card({
  icon,
  title,
  badge,
  children,
}: {
  icon?: ReactNode;
  title: string;
  badge?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-charcoal/15 bg-white p-5 shadow-sm">
      <header className="mb-4 flex items-center gap-2">
        {icon && <span className="text-brand-700">{icon}</span>}
        <h2 className="text-base font-semibold text-charcoal">{title}</h2>
        {badge}
      </header>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

export function PrimaryButton({
  children,
  onClick,
  type = "button",
  disabled,
}: {
  children: ReactNode;
  onClick?: () => void;
  type?: "button" | "submit";
  disabled?: boolean;
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className="inline-flex min-h-[48px] w-full items-center justify-center rounded-xl bg-brand-700 px-6 text-base font-semibold text-white shadow-sm active:bg-brand-800 disabled:opacity-60"
    >
      {children}
    </button>
  );
}

export function SecondaryButton({
  children,
  onClick,
  disabled,
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex min-h-[48px] w-full items-center justify-center rounded-xl border border-charcoal/30 px-6 text-base font-semibold text-charcoal active:bg-field-gray disabled:opacity-60"
    >
      {children}
    </button>
  );
}

/** Editable list of free-text chips (services, certifications, awards, cities). */
export function TagEditor({
  label,
  values,
  onChange,
  placeholder,
}: {
  label: string;
  values: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}) {
  const [input, setInput] = useState("");
  function add() {
    const v = input.trim();
    if (v && !values.includes(v)) onChange([...values, v]);
    setInput("");
  }
  return (
    <div>
      <span className="mb-1 block text-sm font-medium text-charcoal">{label}</span>
      {values.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2">
          {values.map((v) => (
            <span
              key={v}
              className="inline-flex items-center gap-1 rounded-full bg-field-gray px-3 py-1 text-sm text-charcoal"
            >
              {v}
              <button
                type="button"
                onClick={() => onChange(values.filter((x) => x !== v))}
                aria-label={`Remove ${v}`}
                className="text-base leading-none text-charcoal"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          placeholder={placeholder}
          className={inputClass}
        />
        <button
          type="button"
          onClick={add}
          className="shrink-0 rounded-xl border border-brand-700 px-4 text-sm font-semibold text-brand-700 active:bg-brand-50"
        >
          Add
        </button>
      </div>
    </div>
  );
}

/** iOS-style toggle for boolean business rules / delivery channels. */
export function Toggle({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3 py-1">
      <span>
        <span className="block text-sm font-medium text-charcoal">{label}</span>
        {hint && <span className="block text-xs text-charcoal">{hint}</span>}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={() => onChange(!checked)}
        className={cn(
          "relative h-6 w-11 shrink-0 rounded-full transition-colors",
          checked ? "bg-brand-600" : "bg-charcoal/30",
        )}
      >
        <span
          className={cn(
            "absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all",
            checked ? "left-[22px]" : "left-0.5",
          )}
        />
      </button>
    </div>
  );
}

/** Photo grid with broken-URL self-healing (hotlinked images may 404). */
export function PhotoGrid({
  photos,
  onRemove,
}: {
  photos: string[];
  onRemove: (url: string) => void;
}) {
  const [broken, setBroken] = useState<Set<string>>(new Set());
  const shown = photos.filter((p) => !broken.has(p));
  if (shown.length === 0) {
    return (
      <p className="text-sm text-charcoal">
        No photos yet — you can add work photos later from your portal.
      </p>
    );
  }
  return (
    <div className="grid grid-cols-3 gap-2">
      {shown.map((url) => (
        <div key={url} className="relative aspect-square overflow-hidden rounded-lg bg-field-gray">
          <img
            src={url}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover"
            onError={() => setBroken((b) => new Set(b).add(url))}
          />
          <button
            type="button"
            onClick={() => onRemove(url)}
            aria-label="Remove photo"
            className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-sm text-white"
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}

const strokeIcon = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  className: "h-5 w-5",
  "aria-hidden": true,
};

export function ShieldCheckIcon() {
  return (
    <svg {...strokeIcon}>
      <path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3zM9 12l2 2 4-4" />
    </svg>
  );
}

export function MapPinIcon() {
  return (
    <svg {...strokeIcon}>
      <path d="M12 21s7-5.5 7-11a7 7 0 1 0-14 0c0 5.5 7 11 7 11z" />
      <circle cx="12" cy="10" r="2.5" />
    </svg>
  );
}

export function BellIcon() {
  return (
    <svg {...strokeIcon}>
      <path d="M6 9a6 6 0 1 1 12 0c0 5 2 6 2 6H4s2-1 2-6M10 20a2 2 0 0 0 4 0" />
    </svg>
  );
}

export function SlidersIcon() {
  return (
    <svg {...strokeIcon}>
      <path d="M4 6h10M18 6h2M4 12h2M10 12h10M4 18h12M20 18h0M14 6a2 2 0 1 0 0-.01M8 12a2 2 0 1 0 0-.01M16 18a2 2 0 1 0 0-.01" />
    </svg>
  );
}

export function CheckCircleIcon({ className = "h-16 w-16" }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M8.5 12.5l2.5 2.5 4.5-5" />
    </svg>
  );
}

/** Top-of-wizard progress: four named phases with the active one filled. */
export function ProgressHeader({ phaseIndex }: { phaseIndex: number }) {
  const phases = ["Profile", "Account", "Verify", "Preferences"];
  return (
    <ol className="mb-6 flex items-center gap-1.5" aria-label="Onboarding progress">
      {phases.map((name, i) => (
        <li key={name} className="flex flex-1 flex-col items-center gap-1">
          <span
            className={cn(
              "h-1.5 w-full rounded-full",
              i <= phaseIndex ? "bg-brand-600" : "bg-charcoal/15",
            )}
          />
          <span
            className={cn(
              "text-[11px] font-medium",
              i === phaseIndex ? "text-brand-700" : "text-charcoal",
            )}
          >
            {name}
          </span>
        </li>
      ))}
    </ol>
  );
}
